import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SlidersHorizontal, Car } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { VehicleCard } from "@/components/VehicleCard";
import { SearchBar } from "@/components/SearchBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const CATEGORIES = ["SUV", "SUV Compact", "Citadine", "Cabriolet", "Cabriolet 4x4", "Pick-up", "Van 9 places", "Mini 4x4"];

export default function SearchResults() {
  const { t } = useTranslation();
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ category: "", transmission: "", fuel: "", seats: "", min_price: "", max_price: "" });
  const [interest, setInterest] = useState(0);
  const country = sp.get("country");

  useEffect(() => {
    if (country) api.get(`/islands/${country}/interest`).then((r) => setInterest(r.data.count)).catch(() => {});
  }, [country]);

  const showInterest = async () => {
    try { const r = await api.post(`/islands/${country}/interest`); setInterest(r.data.count); toast.success(t("search.interestThanks")); }
    catch (e) { console.error("island interest failed", e); }
  };

  const fetchVehicles = () => {
    setLoading(true);
    const p = new URLSearchParams();
    ["country", "start_date", "end_date", "plate", "pickup"].forEach((k) => { if (sp.get(k)) p.set(k, sp.get(k)); });
    Object.entries(filters).forEach(([k, v]) => { if (v) p.set(k, v); });
    api.get(`/vehicles?${p.toString()}`).then((r) => setVehicles(r.data.vehicles)).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { fetchVehicles(); /* eslint-disable-next-line */ }, [sp]);

  const reset = () => { setFilters({ category: "", transmission: "", fuel: "", seats: "", min_price: "", max_price: "" }); };

  const FilterPanel = () => (
    <div className="bg-white rounded-2xl p-6 shadow-card border border-border space-y-5">
      <h3 className="font-heading font-bold text-lg text-ink flex items-center gap-2"><SlidersHorizontal className="w-5 h-5 text-canard" /> {t("filters.title")}</h3>
      <div>
        <label className="text-xs font-bold uppercase text-muted2">{t("filters.type")}</label>
        <select data-testid="filter-category" value={filters.category} onChange={(e) => setFilters({ ...filters, category: e.target.value })}
          className="w-full h-10 mt-1 rounded-xl border border-border bg-sand px-3 text-sm font-semibold">
          <option value="">—</option>{CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <div>
        <label className="text-xs font-bold uppercase text-muted2">{t("filters.transmission")}</label>
        <select data-testid="filter-transmission" value={filters.transmission} onChange={(e) => setFilters({ ...filters, transmission: e.target.value })}
          className="w-full h-10 mt-1 rounded-xl border border-border bg-sand px-3 text-sm font-semibold">
          <option value="">—</option><option>Automatique</option><option>Manuelle</option>
        </select>
      </div>
      <div>
        <label className="text-xs font-bold uppercase text-muted2">{t("filters.fuel")}</label>
        <select data-testid="filter-fuel" value={filters.fuel} onChange={(e) => setFilters({ ...filters, fuel: e.target.value })}
          className="w-full h-10 mt-1 rounded-xl border border-border bg-sand px-3 text-sm font-semibold">
          <option value="">—</option><option>Essence</option><option>Diesel</option>
        </select>
      </div>
      <div>
        <label className="text-xs font-bold uppercase text-muted2">{t("filters.seats")}</label>
        <Input data-testid="filter-seats" type="number" min="1" value={filters.seats} onChange={(e) => setFilters({ ...filters, seats: e.target.value })} className="mt-1 h-10 rounded-xl bg-sand" />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-xs font-bold uppercase text-muted2">Min €</label>
          <Input data-testid="filter-min-price" type="number" value={filters.min_price} onChange={(e) => setFilters({ ...filters, min_price: e.target.value })} className="mt-1 h-10 rounded-xl bg-sand" />
        </div>
        <div>
          <label className="text-xs font-bold uppercase text-muted2">Max €</label>
          <Input data-testid="filter-max-price" type="number" value={filters.max_price} onChange={(e) => setFilters({ ...filters, max_price: e.target.value })} className="mt-1 h-10 rounded-xl bg-sand" />
        </div>
      </div>
      <div className="flex gap-2 pt-2">
        <Button data-testid="filter-apply-button" onClick={() => { fetchVehicles(); setShowFilters(false); }} className="flex-1 rounded-full bg-canard hover:bg-canard-dark">{t("filters.apply")}</Button>
        <Button data-testid="filter-reset-button" variant="outline" onClick={reset} className="rounded-full">{t("filters.reset")}</Button>
      </div>
    </div>
  );

  return (
    <div className="section-sand min-h-screen">
      <div className="bg-canard-dark py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6"><SearchBar compact /></div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <p className="font-heading font-bold text-xl text-ink">{vehicles.length} {t("filters.results")}</p>
          <Button variant="outline" data-testid="mobile-filters-toggle" onClick={() => setShowFilters(!showFilters)} className="lg:hidden rounded-full">
            <SlidersHorizontal className="w-4 h-4 mr-1" /> {t("filters.title")}
          </Button>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <aside className={`lg:col-span-1 ${showFilters ? "block" : "hidden"} lg:block`}><FilterPanel /></aside>
          <div className="lg:col-span-3">
            {loading ? (
              <div className="text-center py-20 text-canard font-body">{t("common.loading")}</div>
            ) : vehicles.length === 0 ? (
              <div className="text-center py-20 bg-white rounded-2xl border border-border" data-testid="search-empty">
                <Car className="w-12 h-12 text-border mx-auto mb-3" />
                <p className="text-muted2 font-body">{t("common.noResults")}</p>
                {sp.get("country") && (
                  <>
                    <p data-testid="empty-island-message" className="text-canard font-body mt-3 max-w-md mx-auto px-4">{t("search.emptyIsland")}</p>
                    {interest > 0 && <p data-testid="island-interest-count" className="text-emeraude-dark font-bold text-sm mt-3">{t("search.interested", { n: interest })}</p>}
                    <div className="flex flex-wrap items-center justify-center gap-3 mt-4">
                      <button data-testid="island-interest-btn" onClick={showInterest} className="inline-flex items-center gap-1 rounded-full border border-canard text-canard hover:bg-canard/5 font-bold h-11 px-6 text-sm transition-colors">{t("search.imInterested")}</button>
                      <button data-testid="become-host-cta" onClick={() => nav(`/register?role=loueur&island=${encodeURIComponent(country)}`)} className="inline-flex items-center gap-1 rounded-full bg-canard hover:bg-canard-dark text-white font-bold h-11 px-6 text-sm transition-colors">{t("search.becomeHost")}</button>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                {vehicles.map((v, i) => <VehicleCard key={v.id} v={v} index={i} />)}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
